import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { openChatPopup } from "@/lib/chatPopup";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import DesktopAppButton from "@/components/chat/DesktopAppButton";
import {
  MessageSquare,
  Plus,
  Search,
  Circle,
  Loader2,
  Users,
  BellOff,
  Bell,
  Check,
  ChevronDown,
  ChevronRight,
  X,
  Pencil,
  LogOut,
  Star,
} from "lucide-react";
import { useMobileDetect } from "@/hooks/useMobileDetect";
import { useNativeApp } from "@/hooks/useNativeApp";
import { CHAT_ROOM_PURPOSE_META, type ChatRoomPurpose } from "@/types/chat";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { messagePreviewText } from "./chatStickers";
import { useToast } from "@/hooks/use-toast";
import { useSharedChatWebSocket } from "@/contexts/ChatWebSocketContext";
import { useAuthStore } from "@/stores/authStore";
import chatService, { type ChatRoomWithDetails } from "@/services/chatService";
import RoomContentSearchSection from "@/components/chat/RoomContentSearchSection";
import userService from "@/services/userService";
import commonCodeService from "@/services/commonCodeService";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getApiError, getProfileImageUrl } from "@/services/api";

interface ChatSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// 카카오톡 스타일 프로필 아바타 컴포넌트
interface RoomProfileAvatarProps {
  room: ChatRoomWithDetails;
  currentUserId?: string;
}

function RoomProfileAvatar({ room, currentUserId }: RoomProfileAvatarProps) {
  // 현재 사용자를 제외한 멤버들
  const otherMembers = room.members?.filter((m) => m.user_id !== currentUserId) || [];
  const memberCount = otherMembers.length;

  // 프로필 이미지 또는 이니셜 렌더링
  const renderAvatar = (member: typeof otherMembers[0], size: string, fontSize: string) => {
    const userId = member.user_id;
    const hasProfileImage = member.user?.profile_image;
    const name = member.user?.name || "?";

    if (hasProfileImage && userId) {
      return (
        <img
          src={getProfileImageUrl(userId)}
          alt={name}
          className={`${size} rounded-full object-cover`}
          onError={(e) => {
            // 이미지 로드 실패 시 이니셜로 대체
            e.currentTarget.style.display = 'none';
            e.currentTarget.nextElementSibling?.classList.remove('hidden');
          }}
        />
      );
    }
    return (
      <div className={`${size} rounded-full bg-primary/20 flex items-center justify-center`}>
        <span className={`${fontSize} font-medium text-primary`}>{name.charAt(0)}</span>
      </div>
    );
  };

  // 0명 (자기 자신만 있는 경우) - fallback
  if (memberCount === 0) {
    return (
      <div className="h-10 w-10 flex-shrink-0 rounded-full bg-primary/10 flex items-center justify-center">
        <Users className="h-4 w-4 text-muted-foreground" />
      </div>
    );
  }

  // 1명 (1:1 채팅)
  if (memberCount === 1) {
    return (
      <div className="h-10 w-10 flex-shrink-0">
        {renderAvatar(otherMembers[0], "h-10 w-10", "text-sm")}
      </div>
    );
  }

  // 2명
  if (memberCount === 2) {
    return (
      <div className="h-10 w-10 flex-shrink-0 relative">
        <div className="absolute top-0 left-0">
          {renderAvatar(otherMembers[0], "h-6 w-6", "text-[10px]")}
        </div>
        <div className="absolute bottom-0 right-0">
          {renderAvatar(otherMembers[1], "h-6 w-6", "text-[10px]")}
        </div>
      </div>
    );
  }

  // 3명
  if (memberCount === 3) {
    return (
      <div className="h-10 w-10 flex-shrink-0 relative">
        <div className="absolute top-0 left-1/2 -translate-x-1/2">
          {renderAvatar(otherMembers[0], "h-5 w-5", "text-[9px]")}
        </div>
        <div className="absolute bottom-0 left-0">
          {renderAvatar(otherMembers[1], "h-5 w-5", "text-[9px]")}
        </div>
        <div className="absolute bottom-0 right-0">
          {renderAvatar(otherMembers[2], "h-5 w-5", "text-[9px]")}
        </div>
      </div>
    );
  }

  // 4명 이상 - 2x2 그리드
  return (
    <div className="h-10 w-10 flex-shrink-0 grid grid-cols-2 gap-0.5">
      {otherMembers.slice(0, 4).map((member, idx) => (
        <div key={member.user_id || idx}>
          {renderAvatar(member, "h-[18px] w-[18px]", "text-[8px]")}
        </div>
      ))}
    </div>
  );
}

export default function ChatSidebar({ open, onOpenChange }: ChatSidebarProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { isMobile, isTablet } = useMobileDetect();
  const { isWebView } = useNativeApp();
  const [searchQuery, setSearchQuery] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [createRoomOpen, setCreateRoomOpen] = useState(false);
  const [newRoomName, setNewRoomName] = useState("");
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [memberSearchQuery, setMemberSearchQuery] = useState("");
  const [expandedDepts, setExpandedDepts] = useState<Set<string>>(new Set());

  // Context menu state (custom positioned menu)
  const [contextMenu, setContextMenu] = useState<{ room: ChatRoomWithDetails; x: number; y: number } | null>(null);
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState<ChatRoomWithDetails | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [leaveTarget, setLeaveTarget] = useState<ChatRoomWithDetails | null>(null);

  // 모바일 또는 WebView에서는 인라인 모드로 이동
  const isInlineMode = isMobile || isTablet || isWebView;

  // 연결 표시용. 새 메시지의 안읽음·미리보기 반영은 useChatWebSocket 이 ["chat-rooms"] 캐시에 직접 한다
  const { isConnected } = useSharedChatWebSocket();

  // Fetch rooms — 열릴 때 enabled 가 켜지며 1회 조회 (별도 refetch 를 겹쳐 두 번 받던 것 제거)
  const { data: rooms = [], isLoading: roomsLoading } = useQuery({
    queryKey: ["chat-rooms"],
    queryFn: () => chatService.getRooms(),
    enabled: open,
  });

  // Fetch users for room creation (활성 직원만 - 모든 사용자 접근 가능)
  const { data: users = [] } = useQuery({
    queryKey: ["users", "active-employees-chat"],
    queryFn: () => userService.getActiveEmployees(),
    enabled: createRoomOpen,
  });

  // 근무지 공통코드 조회
  const { data: workLocationCodes = [] } = useQuery({
    queryKey: ["common-codes", "WORK_LOCATION"],
    queryFn: () => commonCodeService.getCodeOptions("WORK_LOCATION"),
    enabled: createRoomOpen,
  });

  // Toggle mute mutation (context menu)
  const toggleMuteMutation = useMutation({
    mutationFn: (roomId: number) => chatService.toggleMute(roomId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-rooms"] });
    },
    onError: (error) => {
      toast({
        title: "알림 설정 변경 실패",
        description: getApiError(error).message,
        variant: "destructive",
      });
    },
  });

  // Toggle favorite mutation (context menu)
  const toggleFavoriteMutation = useMutation({
    mutationFn: (roomId: number) => chatService.toggleFavorite(roomId),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["chat-rooms"] });
      toast({ title: data.message });
    },
    onError: (error) => {
      toast({
        title: "즐겨찾기 변경 실패",
        description: getApiError(error).message,
        variant: "destructive",
      });
    },
  });

  // Leave room mutation (context menu)
  const leaveRoomMutation = useMutation({
    mutationFn: (roomId: number) => chatService.leaveRoom(roomId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-rooms"] });
      toast({ title: "채팅방을 나갔습니다" });
      setLeaveDialogOpen(false);
      setLeaveTarget(null);
    },
    onError: (error) => {
      toast({
        title: "채팅방 나가기 실패",
        description: getApiError(error).message,
        variant: "destructive",
      });
    },
  });

  // Rename room mutation (context menu)
  const renameRoomMutation = useMutation({
    mutationFn: ({ roomId, name }: { roomId: number; name: string }) =>
      chatService.updateRoom(roomId, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["chat-rooms"] });
      toast({ title: "채팅방 이름이 변경되었습니다" });
      setRenameDialogOpen(false);
      setRenameTarget(null);
      setRenameValue("");
    },
    onError: (error) => {
      toast({
        title: "이름 변경 실패",
        description: getApiError(error).message,
        variant: "destructive",
      });
    },
  });

  // Create room mutation
  const createRoomMutation = useMutation({
    mutationFn: () =>
      chatService.createRoom({
        name: selectedMembers.length > 1 ? newRoomName || undefined : undefined,
        room_type: selectedMembers.length === 1 ? "direct" : "group",
        member_ids: selectedMembers,
      }),
    onSuccess: (room) => {
      queryClient.invalidateQueries({ queryKey: ["chat-rooms"] });
      setCreateRoomOpen(false);
      setNewRoomName("");
      setSelectedMembers([]);
      setMemberSearchQuery("");
      setExpandedDepts(new Set());
      toast({ title: "채팅방이 생성되었습니다." });
      // Open the new chat room
      if (isInlineMode) {
        // 모바일/WebView: 채팅 페이지로 이동
        navigate(`/chat/popup/${room.id}`);
        onOpenChange(false);
      } else {
        // 데스크톱: 팝업으로 열기
        openChatPopup(room.id);
      }
    },
    onError: (error) => {
      toast({
        title: "채팅방 생성 실패",
        description: getApiError(error).message,
        variant: "destructive",
      });
    },
  });

  // 검색 필터링된 사용자 목록 (본인 제외)
  const filteredUsers = useMemo(() => {
    return users
      .filter((u) => u.id !== user?.id)
      .filter((u) => {
        if (!memberSearchQuery.trim()) return true;
        const query = memberSearchQuery.toLowerCase();
        return (
          u.name?.toLowerCase().includes(query) ||
          u.english_name?.toLowerCase().includes(query) ||
          u.email?.toLowerCase().includes(query)
        );
      });
  }, [users, user?.id, memberSearchQuery]);

  // 근무지 라벨 매핑 (공통코드에서 조회)
  const workLocationLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    workLocationCodes.forEach((code) => {
      labels[code.code] = code.value;
    });
    return labels;
  }, [workLocationCodes]);

  // 근무지별로 사용자 그룹화
  const usersByWorkLocation = useMemo(() => {
    return filteredUsers.reduce<Record<string, typeof filteredUsers>>((acc, u) => {
      const location = u.work_location || "other";
      if (!acc[location]) {
        acc[location] = [];
      }
      acc[location].push(u);
      return acc;
    }, {});
  }, [filteredUsers]);

  // 부서별로 사용자 그룹화
  const usersByDepartment = useMemo(() => {
    return filteredUsers.reduce<Record<string, typeof filteredUsers>>((acc, u) => {
      const deptName = u.department?.name || "부서 미지정";
      if (!acc[deptName]) {
        acc[deptName] = [];
      }
      acc[deptName].push(u);
      return acc;
    }, {});
  }, [filteredUsers]);

  // 부서명 정렬 (부서 미지정은 맨 뒤로)
  const sortedDepartments = useMemo(() => {
    return Object.keys(usersByDepartment).sort((a, b) => {
      if (a === "부서 미지정") return 1;
      if (b === "부서 미지정") return -1;
      return a.localeCompare(b, "ko");
    });
  }, [usersByDepartment]);

  // 부서 확장/접기 토글
  const toggleDeptExpansion = (deptName: string) => {
    setExpandedDepts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(deptName)) {
        newSet.delete(deptName);
      } else {
        newSet.add(deptName);
      }
      return newSet;
    });
  };

  const handleMemberToggle = (userId: string) => {
    setSelectedMembers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  // 부서별 전체 선택
  const selectDeptMembers = (deptName: string) => {
    const deptUserIds = usersByDepartment[deptName]?.map((u) => u.id) || [];
    setSelectedMembers((prev) => {
      const newSet = new Set(prev);
      deptUserIds.forEach((id) => newSet.add(id));
      return Array.from(newSet);
    });
  };

  // 부서별 전체 해제
  const deselectDeptMembers = (deptName: string) => {
    const deptUserIds = new Set(usersByDepartment[deptName]?.map((u) => u.id) || []);
    setSelectedMembers((prev) => prev.filter((id) => !deptUserIds.has(id)));
  };

  // 부서 내 선택된 인원 수
  const getDeptSelectedCount = (deptName: string) => {
    const deptUserIds = new Set(usersByDepartment[deptName]?.map((u) => u.id) || []);
    return selectedMembers.filter((id) => deptUserIds.has(id)).length;
  };

  // 근무지별 전체 선택
  const selectWorkLocationMembers = (location: string) => {
    const locationUserIds = usersByWorkLocation[location]?.map((u) => u.id) || [];
    setSelectedMembers((prev) => {
      const newSet = new Set(prev);
      locationUserIds.forEach((id) => newSet.add(id));
      return Array.from(newSet);
    });
  };

  // 근무지별 전체 해제
  const deselectWorkLocationMembers = (location: string) => {
    const locationUserIds = new Set(usersByWorkLocation[location]?.map((u) => u.id) || []);
    setSelectedMembers((prev) => prev.filter((id) => !locationUserIds.has(id)));
  };

  // 근무지 내 선택된 인원 수
  const getWorkLocationSelectedCount = (location: string) => {
    const locationUserIds = new Set(usersByWorkLocation[location]?.map((u) => u.id) || []);
    return selectedMembers.filter((id) => locationUserIds.has(id)).length;
  };

  // 전체 해제
  const deselectAllMembers = () => {
    setSelectedMembers([]);
  };

  // Long-press & right-click context menu
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressTriggeredRef = useRef(false);
  const contextMenuRef = useRef<HTMLDivElement>(null);

  const openContextMenu = useCallback((room: ChatRoomWithDetails, x: number, y: number) => {
    const menuW = 192, menuH = 140;
    const cx = Math.min(x, window.innerWidth - menuW - 8);
    const cy = Math.min(y, window.innerHeight - menuH - 8);
    setContextMenu({ room, x: cx, y: cy });
  }, []);

  const handleRoomPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>, room: ChatRoomWithDetails) => {
    if (e.button !== 0) return;
    e.preventDefault(); // 텍스트 선택 방지
    longPressTriggeredRef.current = false;
    const clientX = e.clientX;
    const clientY = e.clientY;
    longPressTimerRef.current = setTimeout(() => {
      longPressTimerRef.current = null;
      longPressTriggeredRef.current = true;
      openContextMenu(room, clientX, clientY);
    }, 500);
  }, [openContextMenu]);

  const handleRoomContextMenu = useCallback((e: React.MouseEvent, room: ChatRoomWithDetails) => {
    e.preventDefault();
    openContextMenu(room, e.clientX, e.clientY);
  }, [openContextMenu]);

  const cancelLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  // Close context menu on outside click
  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target as Node)) {
        setContextMenu(null);
      }
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setContextMenu(null);
    };
    document.addEventListener("mousedown", handleClick);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [contextMenu]);

  const handleRoomClick = (roomId: number) => {
    // 클릭한 방의 unread_count를 즉시 0으로 리셋
    queryClient.setQueryData<ChatRoomWithDetails[]>(["chat-rooms"], (old) => {
      if (!old) return old;
      return old.map((room) =>
        room.id === roomId ? { ...room, unread_count: 0 } : room
      );
    });

    if (isInlineMode) {
      // 모바일/WebView: 채팅 페이지로 이동
      navigate(`/chat/popup/${roomId}`);
      onOpenChange(false); // 사이드바 닫기
    } else {
      // 데스크톱: 팝업으로 열기
      openChatPopup(roomId);
    }
  };

  const getRoomDisplayName = (room: ChatRoomWithDetails) => {
    if (room.name) return room.name;
    if (room.room_type === "direct" && room.members) {
      const otherMember = room.members.find((m) => m.user_id !== user?.id);
      return otherMember?.user?.name || "1:1 채팅";
    }
    if (room.members && room.members.length > 0) {
      const memberNames = room.members
        .filter((m) => m.user_id !== user?.id)
        .map((m) => m.user?.name || "Unknown")
        .join(", ");
      if (memberNames.length > 20) {
        return memberNames.substring(0, 20) + "...";
      }
      return memberNames || "그룹 채팅";
    }
    return "그룹 채팅";
  };

  const formatRoomTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === now.toDateString()) {
      return date.toLocaleTimeString("ko-KR", { hour: "2-digit", minute: "2-digit" });
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return "어제";
    }
    if (date.getFullYear() === now.getFullYear()) {
      return date.toLocaleDateString("ko-KR", { month: "numeric", day: "numeric" });
    }
    return date.toLocaleDateString("ko-KR", { year: "2-digit", month: "numeric", day: "numeric" });
  };

  // Helper: check if room is favorited by current user
  const isFavoriteRoom = (room: ChatRoomWithDetails) =>
    room.members?.find(m => m.user_id === user?.id)?.is_favorite ?? false;

  // Filter and sort rooms (즐겨찾기 상단 고정, 안읽음 필터)
  const filteredRooms = rooms
    .filter((room) => {
      if (unreadOnly && (room.unread_count ?? 0) === 0) return false;
      return getRoomDisplayName(room).toLowerCase().includes(searchQuery.toLowerCase());
    })
    .sort((a, b) => {
      const aFav = isFavoriteRoom(a) ? 1 : 0;
      const bFav = isFavoriteRoom(b) ? 1 : 0;
      if (aFav !== bFav) return bFav - aFav;
      const aTime = a.last_message?.created_at || a.created_at;
      const bTime = b.last_message?.created_at || b.created_at;
      return new Date(bTime).getTime() - new Date(aTime).getTime();
    });

  // Calculate total unread count
  const totalUnreadCount = rooms.reduce((sum, room) => sum + (room.unread_count ?? 0), 0);

  // 검색 중 여부 (대화 내용 검색 섹션 표시)
  const isSearching = searchQuery.trim().length > 0;

  // 안읽음이 0이 되면 필터 자동 해제
  useEffect(() => {
    if (totalUnreadCount === 0 && unreadOnly) {
      setUnreadOnly(false);
    }
  }, [totalUnreadCount, unreadOnly]);

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full max-w-[400px] sm:max-w-[440px] !p-0 z-[10001]" overlayClassName="z-[10000]">
          <SheetHeader className="px-4 py-3 border-b">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5" />
                <SheetTitle className="text-base">메시지</SheetTitle>
                {isConnected ? (
                  <Circle className="h-2 w-2 fill-green-500 text-green-500" />
                ) : (
                  <Circle className="h-2 w-2 fill-red-500 text-red-500" />
                )}
                {totalUnreadCount > 0 && (
                  <Badge
                    variant={unreadOnly ? "default" : "destructive"}
                    className="h-5 min-w-5 rounded-full p-0 text-xs flex items-center justify-center cursor-pointer select-none"
                    onClick={() => setUnreadOnly((prev) => !prev)}
                    title={unreadOnly ? "전체 채팅방 보기" : "안읽은 채팅방만 보기"}
                  >
                    {totalUnreadCount > 99 ? "99+" : totalUnreadCount}
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-1.5 mr-6">
                <DesktopAppButton className="h-7 text-xs px-2" onNavigate={() => onOpenChange(false)} />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  onClick={() => setCreateRoomOpen(true)}
                >
                  <Plus className="h-3 w-3 mr-1" />
                  새채팅
                </Button>
              </div>
            </div>
          </SheetHeader>

          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="채팅방 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
            </div>
          </div>

          <div className="h-[calc(100vh-140px)] overflow-y-auto overflow-x-hidden">
            {roomsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredRooms.length === 0 && !isSearching ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageSquare className="mx-auto h-8 w-8 mb-2" />
                <p className="text-sm">{unreadOnly ? "안읽은 채팅방이 없습니다" : "채팅방이 없습니다"}</p>
                {unreadOnly && (
                  <Button variant="link" size="sm" className="mt-1 text-xs" onClick={() => setUnreadOnly(false)}>
                    전체 보기
                  </Button>
                )}
              </div>
            ) : (
              <>
              {isSearching && filteredRooms.length > 0 && (
                <div className="flex items-center gap-1.5 px-4 py-2 bg-muted/40 border-b">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs font-medium text-muted-foreground">채팅방</span>
                </div>
              )}
              {filteredRooms.map((room) => {
                const isMuted = room.members?.find(m => m.user_id === user?.id)?.is_muted;

                const hasUnread = (room.unread_count ?? 0) > 0;

                return (
                  <div
                    key={room.id}
                    onClick={() => {
                      if (longPressTriggeredRef.current) {
                        longPressTriggeredRef.current = false;
                        return;
                      }
                      handleRoomClick(room.id);
                    }}
                    onPointerDown={(e) => handleRoomPointerDown(e, room)}
                    onPointerUp={cancelLongPress}
                    onPointerLeave={cancelLongPress}
                    onContextMenu={(e) => handleRoomContextMenu(e, room)}
                    className={`flex cursor-pointer items-center gap-3 px-3 py-3 hover:bg-muted/50 border-b transition-colors active:bg-muted select-none overflow-hidden ${hasUnread ? "bg-blue-50 dark:bg-blue-950/30" : ""}`}
                  >
                    <div className="flex-shrink-0">
                      <RoomProfileAvatar room={room} currentUserId={user?.id} />
                    </div>
                    <div className="flex-1 overflow-hidden" style={{ minWidth: 0 }}>
                      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-1">
                        <div className="flex items-center gap-1 overflow-hidden min-w-0">
                          <span className={`truncate text-sm ${hasUnread ? "font-bold text-foreground" : "font-medium"}`}>{getRoomDisplayName(room)}</span>
                          {room.purpose && CHAT_ROOM_PURPOSE_META[room.purpose as ChatRoomPurpose] && (
                            <>
                              <span className="flex-shrink-0 rounded bg-sky-100 px-1 py-px text-[10px] font-medium text-sky-800">
                                {CHAT_ROOM_PURPOSE_META[room.purpose as ChatRoomPurpose].label}
                              </span>
                              {(room.pending_count ?? 0) > 0 && (
                                <span className="flex-shrink-0 rounded bg-amber-100 px-1 py-px text-[10px] font-medium text-amber-800"
                                  title="답변이 등록되기 전까지 남는 배지입니다">
                                  {CHAT_ROOM_PURPOSE_META[room.purpose as ChatRoomPurpose].pendingLabel} {room.pending_count}
                                </span>
                              )}
                            </>
                          )}
                          {room.room_type !== "direct" && (room.member_count || room.members?.length) && (
                            <span className="text-xs text-muted-foreground flex-shrink-0">
                              ({room.member_count || room.members?.length})
                            </span>
                          )}
                          {isFavoriteRoom(room) && (
                            <Star className="h-3 w-3 fill-yellow-400 text-yellow-400 flex-shrink-0" />
                          )}
                          {isMuted && (
                            <BellOff className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          )}
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          {hasUnread && (
                            <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-red-500 text-white text-[11px] font-semibold">
                              {room.unread_count}
                            </span>
                          )}
                          {room.last_message && (
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {formatRoomTime(room.last_message.created_at)}
                            </span>
                          )}
                        </div>
                      </div>
                      <p className={`text-sm truncate mt-0.5 ${room.last_message ? (hasUnread ? "text-foreground" : "text-muted-foreground") : "text-muted-foreground"}`}>
                        {room.last_message ? messagePreviewText(room.last_message.message_type, room.last_message.content) : "대화를 시작해보세요"}
                      </p>
                    </div>
                  </div>
                );
              })}
              {isSearching && (
                <RoomContentSearchSection
                  searchQuery={searchQuery}
                  rooms={rooms}
                  currentUserId={user?.id}
                  getRoomDisplayName={getRoomDisplayName}
                  onRoomSelect={handleRoomClick}
                  hasNameMatches={filteredRooms.length > 0}
                />
              )}
              </>
            )}
          </div>

          {/* Custom context menu */}
          {contextMenu && (
            <div
              ref={contextMenuRef}
              className="fixed z-[10002] min-w-[180px] rounded-md border bg-popover p-1 shadow-md animate-in fade-in-0 zoom-in-95 select-none"
              style={{ top: contextMenu.y, left: contextMenu.x }}
            >
              <button
                className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                onClick={() => {
                  toggleFavoriteMutation.mutate(contextMenu.room.id);
                  setContextMenu(null);
                }}
              >
                {contextMenu.room.members?.find(m => m.user_id === user?.id)?.is_favorite ? (
                  <><Star className="mr-2 h-4 w-4 fill-yellow-400 text-yellow-400" />즐겨찾기 해제</>
                ) : (
                  <><Star className="mr-2 h-4 w-4" />즐겨찾기</>
                )}
              </button>
              <button
                className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                onClick={() => {
                  toggleMuteMutation.mutate(contextMenu.room.id);
                  setContextMenu(null);
                }}
              >
                {contextMenu.room.members?.find(m => m.user_id === user?.id)?.is_muted ? (
                  <>
                    <Bell className="mr-2 h-4 w-4" />
                    알림 켜기
                  </>
                ) : (
                  <>
                    <BellOff className="mr-2 h-4 w-4" />
                    알림 끄기
                  </>
                )}
              </button>
              {contextMenu.room.room_type !== "direct" && (
                <button
                  className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent hover:text-accent-foreground"
                  onClick={() => {
                    setRenameTarget(contextMenu.room);
                    setRenameValue(contextMenu.room.name || "");
                    setRenameDialogOpen(true);
                    setContextMenu(null);
                  }}
                >
                  <Pencil className="mr-2 h-4 w-4" />
                  이름 변경
                </button>
              )}
              <div className="my-1 h-px bg-border" />
              <button
                className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm text-destructive hover:bg-accent"
                onClick={() => {
                  setLeaveTarget(contextMenu.room);
                  setLeaveDialogOpen(true);
                  setContextMenu(null);
                }}
              >
                <LogOut className="mr-2 h-4 w-4" />
                나가기
              </button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Rename Room Dialog */}
      <Dialog open={renameDialogOpen} onOpenChange={(open) => {
        setRenameDialogOpen(open);
        if (!open) { setRenameTarget(null); setRenameValue(""); }
      }}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-sm mx-auto z-[10003]" overlayClassName="z-[10002]">
          <DialogHeader>
            <DialogTitle>채팅방 이름 변경</DialogTitle>
            <DialogDescription>새로운 채팅방 이름을 입력하세요.</DialogDescription>
          </DialogHeader>
          <Input
            placeholder="채팅방 이름"
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && renameValue.trim() && renameTarget) {
                renameRoomMutation.mutate({ roomId: renameTarget.id, name: renameValue.trim() });
              }
            }}
            autoFocus
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameDialogOpen(false)}>취소</Button>
            <Button
              onClick={() => {
                if (renameTarget && renameValue.trim()) {
                  renameRoomMutation.mutate({ roomId: renameTarget.id, name: renameValue.trim() });
                }
              }}
              disabled={!renameValue.trim() || renameRoomMutation.isPending}
            >
              {renameRoomMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              변경
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave Room AlertDialog */}
      <AlertDialog open={leaveDialogOpen} onOpenChange={(open) => {
        setLeaveDialogOpen(open);
        if (!open) setLeaveTarget(null);
      }}>
        <AlertDialogContent className="z-[10003]" overlayClassName="z-[10002]">
          <AlertDialogHeader>
            <AlertDialogTitle>채팅방 나가기</AlertDialogTitle>
            <AlertDialogDescription>
              정말 이 채팅방을 나가시겠습니까? 대화 내용은 더 이상 확인할 수 없습니다.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>취소</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (leaveTarget) leaveRoomMutation.mutate(leaveTarget.id);
              }}
            >
              나가기
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Room Dialog */}
      <Dialog open={createRoomOpen} onOpenChange={setCreateRoomOpen}>
        <DialogContent className="w-[calc(100%-2rem)] max-w-md mx-auto z-[10003]" overlayClassName="z-[10002]">
          <DialogHeader>
            <DialogTitle>새 채팅방</DialogTitle>
            <DialogDescription>새로운 채팅방을 만듭니다.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>참여자 선택</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="이름 또는 이메일로 검색..."
                  value={memberSearchQuery}
                  onChange={(e) => setMemberSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              {/* 근무지별 선택 버튼 */}
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(usersByWorkLocation).map(([location, locationUsers]) => {
                  const selectedCount = getWorkLocationSelectedCount(location);
                  const isAllSelected = selectedCount === locationUsers.length && locationUsers.length > 0;
                  const label = workLocationLabels[location] || location;

                  return (
                    <Button
                      key={location}
                      type="button"
                      variant={isAllSelected ? "default" : "outline"}
                      size="sm"
                      className="h-7 px-2.5 text-xs"
                      onClick={() => {
                        if (isAllSelected) {
                          deselectWorkLocationMembers(location);
                        } else {
                          selectWorkLocationMembers(location);
                        }
                      }}
                    >
                      {label} ({locationUsers.length})
                      {selectedCount > 0 && !isAllSelected && (
                        <span className="ml-1 text-primary">+{selectedCount}</span>
                      )}
                    </Button>
                  );
                })}
                {selectedMembers.length > 0 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs text-muted-foreground"
                    onClick={deselectAllMembers}
                  >
                    <X className="h-3.5 w-3.5 mr-0.5" />
                    전체해제
                  </Button>
                )}
              </div>
              <ScrollArea className="h-60 border rounded-lg">
                {filteredUsers.length === 0 ? (
                  <div className="flex items-center justify-center h-full text-muted-foreground py-8">
                    <p className="text-sm">참여자가 없습니다</p>
                  </div>
                ) : (
                  <div className="p-2">
                    {sortedDepartments.map((deptName) => {
                      const deptUsers = usersByDepartment[deptName] || [];
                      const deptSelectedCount = getDeptSelectedCount(deptName);
                      const isAllSelected = deptSelectedCount === deptUsers.length && deptUsers.length > 0;

                      return (
                        <Collapsible
                          key={deptName}
                          open={expandedDepts.has(deptName)}
                          onOpenChange={() => toggleDeptExpansion(deptName)}
                        >
                          <div className="flex items-center gap-1">
                            <CollapsibleTrigger asChild>
                              <Button
                                variant="ghost"
                                className="flex-1 justify-start px-2 py-1.5 h-auto font-medium text-sm"
                              >
                                {expandedDepts.has(deptName) ? (
                                  <ChevronDown className="h-4 w-4 mr-2" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 mr-2" />
                                )}
                                {deptName} ({deptUsers.length}명)
                                {deptSelectedCount > 0 && (
                                  <span className="ml-2 text-xs text-primary">
                                    {deptSelectedCount}명 선택
                                  </span>
                                )}
                              </Button>
                            </CollapsibleTrigger>
                            <Button
                              type="button"
                              variant={isAllSelected ? "secondary" : "outline"}
                              size="sm"
                              className="h-6 px-2 text-xs"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isAllSelected) {
                                  deselectDeptMembers(deptName);
                                } else {
                                  selectDeptMembers(deptName);
                                }
                              }}
                            >
                              {isAllSelected ? "해제" : "전체"}
                            </Button>
                          </div>
                          <CollapsibleContent>
                            <div className="ml-6 space-y-0.5">
                              {deptUsers.map((u) => {
                                const isSelected = selectedMembers.includes(u.id);
                                return (
                                  <button
                                    key={u.id}
                                    type="button"
                                    onClick={() => handleMemberToggle(u.id)}
                                    className={cn(
                                      "w-full flex items-center justify-between px-2 py-1.5 rounded text-sm hover:bg-muted",
                                      isSelected && "bg-primary/10"
                                    )}
                                  >
                                    <span className="flex items-center gap-2">
                                      <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                                        {u.profile_image ? (
                                          <img
                                            src={getProfileImageUrl(u.id)}
                                            alt={u.name || ""}
                                            className="h-full w-full object-cover"
                                            onError={(e) => {
                                              e.currentTarget.style.display = 'none';
                                            }}
                                          />
                                        ) : (
                                          <span className="text-[10px] font-medium">
                                            {u.name?.charAt(0) || "?"}
                                          </span>
                                        )}
                                      </div>
                                      <span>{u.name}</span>
                                      {u.english_name && (
                                        <span className="text-xs text-muted-foreground">
                                          ({u.english_name})
                                        </span>
                                      )}
                                    </span>
                                    {isSelected && <Check className="h-4 w-4 text-primary" />}
                                  </button>
                                );
                              })}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
              {selectedMembers.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  {selectedMembers.length}명 선택됨 • {selectedMembers.length === 1 ? "1:1 채팅" : "그룹 채팅"}
                </p>
              )}
            </div>

            {selectedMembers.length > 1 && (
              <div className="space-y-2">
                <Label htmlFor="roomName">채팅방 이름 (선택)</Label>
                <Input
                  id="roomName"
                  placeholder="채팅방 이름을 입력하세요"
                  value={newRoomName}
                  onChange={(e) => setNewRoomName(e.target.value)}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setCreateRoomOpen(false);
                setMemberSearchQuery("");
                setSelectedMembers([]);
                setExpandedDepts(new Set());
              }}
            >
              취소
            </Button>
            <Button
              onClick={() => createRoomMutation.mutate()}
              disabled={createRoomMutation.isPending || selectedMembers.length === 0}
            >
              {createRoomMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              만들기
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
